import { type PointerEvent, useEffect, useRef } from "react";

/** Long enough that crossing the mark on the way somewhere sets nothing off. */
const ARRIVE_MS = 160;

/** Short enough to feel like leaving, long enough to survive a wandering hand. */
const LEAVE_MS = 220;

export type HoverIntent = {
  onPointerEnter: (event: PointerEvent) => void;
  onPointerLeave: (event: PointerEvent) => void;
};

/**
 * Hovering brings the column in; leaving takes it away again. Both wait, for
 * opposite reasons: crossing the mark on the way somewhere should set nothing
 * off, and a hand that wanders off the edge of a row should not lose what it
 * was reading.
 *
 * A touch reports a pointer entering and never leaving, so it would bring the
 * column in and strand it there. Down there the mark is a press and nothing
 * else.
 *
 * `stillHovered` is what makes the column answer to the pointer and nothing
 * else. Pressing a way navigates, and a screen travelling swaps the page for
 * a snapshot of itself and back — which reads as a pointer leaving and
 * returning even though the hand never moved. Asking where the pointer
 * actually is, at the moment the column would go, means only a hand that has
 * really left takes it.
 */
export function useHoverIntent(arrive: () => void, leave: () => void, stillHovered: () => boolean) {
  const waiting = useRef<ReturnType<typeof setTimeout> | null>(null);

  function cancel() {
    if (waiting.current) clearTimeout(waiting.current);
  }

  function after(ms: number, run: () => void) {
    cancel();
    waiting.current = setTimeout(run, ms);
  }

  useEffect(
    () => () => {
      if (waiting.current) clearTimeout(waiting.current);
    },
    [],
  );

  return {
    cancel,
    handlers: {
      onPointerEnter: (event: PointerEvent) =>
        event.pointerType === "mouse" && after(ARRIVE_MS, arrive),
      onPointerLeave: (event: PointerEvent) =>
        event.pointerType === "mouse" &&
        after(LEAVE_MS, () => {
          if (!stillHovered()) leave();
        }),
    } satisfies HoverIntent,
  };
}
